import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';

export default function CapturaRapida() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const esSuperadmin = user?.rol === 'superadmin';

  const [catalogos, setCatalogos] = useState({ secciones: [], estructuras: [] });
  const [loading, setLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [telefonoError, setTelefonoError] = useState('');
  const [form, setForm] = useState({
    nombre: '', apellido_paterno: '', apellido_materno: '',
    clave_elector: '', telefono_principal: '', seccion_id: '',
    domicilio_breve: '',
    nivel_apoyo: '', ocupacion_general: '', profesion_corta: '',
    disponibilidad_servir: '', tipo_participacion_interes: '',
    area_interes: '', es_talento_potencial: false,
    observaciones: '', estructura_id: '',
    // Domicilio completo
    calle: '', numero_exterior: '', numero_interior: '',
    colonia: '', localidad: 'Zacatlán', municipio: 'Zacatlán',
    codigo_postal: '', referencias: ''
  });

  useEffect(() => {
    const fetchCatalogos = async () => {
      try {
        const [catRes, estRes] = await Promise.all([
          api.get('/estructuras/catalogos'),
          api.get('/estructuras')
        ]);
        setCatalogos({
          secciones: catRes.data.secciones,
          estructuras: estRes.data.estructuras
        });
      } catch (error) {
        console.error('Error al cargar catálogos:', error);
      }
    };
    fetchCatalogos();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'telefono_principal') {
      const soloDigitos = value.replace(/\D/g, '').slice(0, 10);
      setForm(prev => ({ ...prev, telefono_principal: soloDigitos }));
      if (soloDigitos.length > 0 && soloDigitos.length < 10) {
        setTelefonoError(`Faltan ${10 - soloDigitos.length} dígito(s)`);
      } else {
        setTelefonoError('');
      }
      return;
    }

    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.telefono_principal.length !== 10) {
      setTelefonoError('El teléfono debe tener exactamente 10 dígitos');
      return;
    }

    setLoading(true);
    setResultado(null);
    try {
      const response = await api.post('/capturas/promovido', form);
      setResultado(response.data);
      if (response.data.estatus === 'exitoso') {
        setForm({
          nombre: '', apellido_paterno: '', apellido_materno: '',
          clave_elector: '', telefono_principal: '', seccion_id: '',
          domicilio_breve: '',
          nivel_apoyo: '', ocupacion_general: '', profesion_corta: '',
          disponibilidad_servir: '', tipo_participacion_interes: '',
          area_interes: '', es_talento_potencial: false,
          observaciones: '', estructura_id: '',
          calle: '', numero_exterior: '', numero_interior: '',
          colonia: '', localidad: 'Zacatlán', municipio: 'Zacatlán',
          codigo_postal: '', referencias: ''
        });
        setTelefonoError('');
      }
    } catch (error) {
      setResultado({ estatus: 'error', mensaje: error.response?.data?.error || 'Error al capturar.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <img src={logo} alt="DOMINIUM" className="h-16" />
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Volver al Dashboard
        </button>
      </header>

      <main className="p-6 max-w-2xl mx-auto">

        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Captura Rápida</h2>
          <p className="text-gray-500 text-sm">Registro de promovido en campo</p>
        </div>

        {resultado && (
          <div className={`mb-6 p-4 rounded-xl border ${
            resultado.estatus === 'exitoso' ? 'bg-green-50 border-green-200 text-green-800' :
            resultado.estatus === 'duplicado' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' :
            'bg-red-50 border-red-200 text-red-800'
          }`}>
            <p className="font-semibold text-lg">
              {resultado.estatus === 'exitoso' ? '✅ Registro exitoso' :
               resultado.estatus === 'duplicado' ? '⚠️ Duplicado detectado' : '❌ Error'}
            </p>
            <p className="text-sm mt-1">{resultado.mensaje}</p>
            {resultado.enviado_a_talento && (
              <p className="text-sm mt-1 font-medium">⭐ Enviado al Banco de Talento</p>
            )}
            {resultado.tipo_duplicidad && (
              <p className="text-sm mt-1">Tipo: {resultado.tipo_duplicidad}</p>
            )}
            {/* ⚠️ Aviso de sección inválida — 11.2 PDF */}
            {resultado.estatus === 'duplicado' && resultado.seccion_correcta === false && (
              <div className="mt-3 p-3 bg-red-100 border border-red-300 rounded-lg">
                <p className="font-bold text-red-800 text-sm">🚫 Coincidencia territorial inválida</p>
                <p className="text-red-700 text-xs mt-1">
                  Esta persona pertenece a una sección diferente a la que estás capturando.
                  La captura queda en revisión y <strong>no suma como promovido activo</strong>.
                </p>
              </div>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm p-6 space-y-5">

          {/* Datos básicos */}
          <div className="border-b pb-4">
            <h3 className="font-semibold text-gray-700 mb-4" style={{ color: '#14532d' }}>
              Datos del Promovido
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input name="nombre" value={form.nombre} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apellido Paterno *</label>
                <input name="apellido_paterno" value={form.apellido_paterno} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Apellido Materno</label>
                <input name="apellido_materno" value={form.apellido_materno} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono *</label>
                <input
                  name="telefono_principal"
                  value={form.telefono_principal}
                  onChange={handleChange}
                  inputMode="numeric"
                  maxLength={10}
                  placeholder="10 dígitos"
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition
                    ${telefonoError
                      ? 'border-red-400 focus:ring-red-400'
                      : form.telefono_principal.length === 10
                        ? 'border-green-400 focus:ring-green-500'
                        : 'border-gray-300 focus:ring-green-500'
                    }`}
                  required
                />
                <div className="flex justify-between items-center mt-1">
                  {telefonoError
                    ? <p className="text-xs text-red-500">{telefonoError}</p>
                    : <span />
                  }
                  <p className={`text-xs ml-auto ${
                    form.telefono_principal.length === 10 ? 'text-green-600' : 'text-gray-400'
                  }`}>
                    {form.telefono_principal.length}/10
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Clave de Elector</label>
                <input name="clave_elector" value={form.clave_elector} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sección Electoral *</label>
                <select name="seccion_id" value={form.seccion_id} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required>
                  <option value="">Seleccionar...</option>
                  {catalogos.secciones.map(s => (
                    <option key={s.id} value={s.id}>{s.numero_seccion} - {s.municipio}</option>
                  ))}
                </select>
              </div>

              {/* ── DOMICILIO BREVE — ocupa las dos columnas ── */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domicilio Breve
                  <span className="text-gray-400 font-normal ml-1">(Ej: Calle Hidalgo 23, Col. Centro)</span>
                </label>
                <input
                  name="domicilio_breve"
                  value={form.domicilio_breve}
                  onChange={handleChange}
                  placeholder="Calle, número y colonia"
                  maxLength={200}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <p className="text-xs text-gray-400 mt-1 text-right">
                  {form.domicilio_breve.length}/200
                </p>
              </div>

              {/* ── DOMICILIO COMPLETO — sección expandible ── */}
              <div className="md:col-span-2">
                <details className="group">
                  <summary className="cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1 select-none">
                    📍 + Agregar domicilio completo <span className="text-gray-400 font-normal">(opcional)</span>
                  </summary>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-blue-50 p-4 rounded-xl border border-blue-100">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Calle</label>
                      <input name="calle" value={form.calle} onChange={handleChange}
                        placeholder="Nombre de la calle"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Núm. Ext.</label>
                        <input name="numero_exterior" value={form.numero_exterior} onChange={handleChange}
                          placeholder="23"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Núm. Int.</label>
                        <input name="numero_interior" value={form.numero_interior} onChange={handleChange}
                          placeholder="A"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Colonia / Barrio</label>
                      <input name="colonia" value={form.colonia} onChange={handleChange}
                        placeholder="Col. Centro"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Código Postal</label>
                      <input name="codigo_postal" value={form.codigo_postal} onChange={handleChange}
                        placeholder="73310"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Referencias / Cómo llegar</label>
                      <input name="referencias" value={form.referencias} onChange={handleChange}
                        placeholder="Casa color amarillo, frente a la tienda OXXO..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
                    </div>
                  </div>
                </details>
              </div>

            </div>
          </div>

          {/* Perfil Político */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-4" style={{ color: '#14532d' }}>Perfil Político</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nivel de Apoyo *</label>
                <select name="nivel_apoyo" value={form.nivel_apoyo} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  required>
                  <option value="">Seleccionar...</option>
                  <option value="seguro">Seguro</option>
                  <option value="simpatizante">Simpatizante</option>
                  <option value="indeciso">Indeciso</option>
                  <option value="oposicion">Oposición</option>
                  <option value="no_definido">No definido</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ocupación</label>
                <select name="ocupacion_general" value={form.ocupacion_general} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">Seleccionar...</option>
                  <option value="comerciante">Comerciante</option>
                  <option value="docente">Docente</option>
                  <option value="estudiante">Estudiante</option>
                  <option value="profesionista">Profesionista</option>
                  <option value="ama_de_casa">Ama de casa</option>
                  <option value="campesino">Campesino</option>
                  <option value="transportista">Transportista</option>
                  <option value="servidor_publico">Servidor público</option>
                  <option value="empleado_privado">Empleado privado</option>
                  <option value="empresario">Empresario</option>
                  <option value="oficio">Oficio</option>
                  <option value="desempleado">Desempleado</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
              {(form.ocupacion_general === 'profesionista' || form.ocupacion_general === 'otro') && (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">¿Cuál profesión?</label>
                  <input name="profesion_corta" value={form.profesion_corta} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
                </div>
              )}

              {/* Estructura solo superadmin */}
              {esSuperadmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Estructura</label>
                  <select name="estructura_id" value={form.estructura_id} onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                    <option value="">Seleccionar...</option>
                    {catalogos.estructuras.map(e => (
                      <option key={e.id} value={e.id}>{e.nombre_estructura}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Banco de Talento */}
          <div className="border-b pb-4">
            <h3 className="font-semibold mb-4" style={{ color: '#5c1022' }}>Banco de Talento Zacateco</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ¿Disponibilidad de servir a Zacatlán?
                </label>
                <select name="disponibilidad_servir" value={form.disponibilidad_servir} onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                  <option value="">Seleccionar...</option>
                  <option value="si">Sí</option>
                  <option value="tal_vez">Tal vez</option>
                  <option value="no">No</option>
                </select>
              </div>
              {(form.disponibilidad_servir === 'si' || form.disponibilidad_servir === 'tal_vez') && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de participación</label>
                    <select name="tipo_participacion_interes" value={form.tipo_participacion_interes} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      <option value="gobierno_municipal">Trabajo en gobierno municipal</option>
                      <option value="proyectos_comunitarios">Proyectos comunitarios</option>
                      <option value="apoyo_tecnico">Apoyo técnico o profesional</option>
                      <option value="organizacion_territorial">Organización territorial</option>
                      <option value="voluntariado">Actividades sociales o voluntariado</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Área de interés</label>
                    <select name="area_interes" value={form.area_interes} onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
                      <option value="">Seleccionar...</option>
                      <option value="educacion">Educación</option>
                      <option value="salud">Salud</option>
                      <option value="campo">Campo</option>
                      <option value="turismo">Turismo</option>
                      <option value="comercio">Comercio</option>
                      <option value="juventud">Juventud</option>
                      <option value="deporte">Deporte</option>
                      <option value="cultura">Cultura</option>
                      <option value="desarrollo_social">Desarrollo social</option>
                      <option value="obras_servicios">Obras y servicios</option>
                      <option value="seguridad">Seguridad</option>
                      <option value="administracion">Administración</option>
                      <option value="juridico">Jurídico</option>
                      <option value="comunicacion">Comunicación</option>
                      <option value="otra">Otra</option>
                    </select>
                  </div>
                </>
              )}
              <div className="flex items-center gap-3 md:col-span-2">
                <input type="checkbox" name="es_talento_potencial" id="talento"
                  checked={form.es_talento_potencial} onChange={handleChange}
                  className="w-4 h-4 rounded" />
                <label htmlFor="talento" className="text-sm font-medium text-gray-700">
                  ⭐ Marcar como talento potencial
                </label>
              </div>
            </div>
          </div>

          {/* Observaciones */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
            <textarea name="observaciones" value={form.observaciones} onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500" />
          </div>

          <button type="submit" disabled={loading}
            className="w-full text-white font-semibold py-3 rounded-xl transition duration-200 disabled:opacity-50 hover:opacity-90"
            style={{ background: 'linear-gradient(135deg, #16a34a 0%, #9b1c35 100%)' }}>
            {loading ? 'Guardando...' : '✅ Registrar Promovido'}
          </button>

        </form>

        <p className="text-center text-gray-400 text-xs mt-4">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>

      </main>
    </div>
  );
}
